import type { DocPageContent } from '../docs-content'

const page = {
  order: 11,
  slug: 'text-captions-subtitles',
  title: 'Texto, subtítulos generados y subtítulos',
  description:
    'Agrega y da estilo a clips de texto, genera subtítulos generados a partir de una transcripción, e importa pistas de subtítulos incrustados.',
  category: 'Herramientas Creativas',
  related: ['shapes-masks', 'properties', 'local-ai'],
  sections: [
    {
      title: 'Tres cosas relacionadas',
      blocks: [
        {
          kind: 'table',
          headers: ['Tipo', 'Qué es', 'Se origina en'],
          rows: [
            ['Clip de texto', 'Un elemento independiente de la línea de tiempo en el que escribes', 'La pestaña Texto'],
            ['Subtítulos generados (captions)', 'Líneas generadas que acompañan a un clip de video o audio', 'Una transcripción de clip'],
            [
              'Subtítulos (subtitles)',
              'Una pista de cues en la línea de tiempo',
              'Una pista incrustada, o subtítulos generados consolidados',
            ],
          ],
        },
      ],
    },
    {
      title: 'Agregar y dar estilo a un clip de texto',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Abre la pestaña **Texto** y haz clic en **Agregar texto** o en una plantilla para colocar texto en el cursor de reproducción, o arrastra una plantilla a la línea de tiempo o al lienzo.',
            'Elige un diseño (**Único**, **2 tramos**, **3 tramos**) y un preajuste de estilo como Limpio, Tercio inferior, Cinemático o Neón.',
            'En la sección **Texto**, define la fuente, el tamaño (8–500 px), el grosor, el color, el fondo, la alineación, el espaciado, la altura de línea, el relleno interno y el radio.',
            'Agrega sombra o contorno en **Efectos**, y anima el texto en **Animación de texto**.',
          ],
        },
      ],
    },
    {
      title: 'Texto en movimiento',
      blocks: [
        {
          kind: 'paragraph',
          text: 'En la sección **Animación de texto** (en las propiedades del clip de texto, o en la etapa de Texto del espacio de trabajo **Animar**), cada carácter, palabra o línea puede animarse de forma independiente. El movimiento se calcula en el momento del renderizado, así que no hay fotogramas clave que gestionar.',
        },
        {
          kind: 'steps',
          items: [
            'Selecciona un clip de texto y abre la sección **Animación de texto**.',
            'Elige un preajuste en la fila **Entrada**, **Salida** o **Bucle**. **Entrada** se reproduce al inicio del clip, **Salida** al final, y **Bucle** se repite continuamente entre ambos.',
            'Haz clic de nuevo en el preajuste activo (o en su ✕) para quitarlo; un preajuste por fila.',
            'Ajusta los controles que aparecen bajo el preajuste activo.',
          ],
        },
        {
          kind: 'table',
          headers: ['Control', 'Qué hace'],
          rows: [
            ['Aplica a', 'La unidad que anima cada preajuste: Carácter, Palabra, Línea o Clip completo.'],
            ['Duración', 'Duración, en fotogramas, de la animación de cada unidad.'],
            ['Escalonado', 'Fotogramas de retraso entre cada unidad, para un efecto en cascada.'],
            ['Orden', 'Qué unidad se anima primero: Hacia adelante, Hacia atrás, Desde el centro hacia afuera, o Aleatorio.'],
            ['Intensidad', 'Escala la fuerza del efecto, de 0 a 200 por ciento.'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Los preajustes de Entrada incluyen Máquina de escribir, Aparecer hacia arriba, Elevarse, Cascada, Aparición súbita (Pop), Difuminado de entrada, Revelado deslizante y Onda de entrada; los de Salida incluyen Desvanecer hacia abajo, Hundirse, Desaparición súbita (Pop Out), Difuminado de salida y Borrado; los de Bucle incluyen Pulso, Onda, Destello y Balanceo.',
        },
      ],
    },
    {
      title: 'Subtítulos generados a partir de una transcripción',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Genera una transcripción del clip desde la biblioteca de medios o el panel de Transcripción.',
            'Usa **Generar subtítulos** (o deja que CachoCut active automáticamente los subtítulos de la transcripción) desde el menú contextual del clip.',
            'Edita el tiempo y el texto de los cues, y elige un preajuste de estilo (Netflix, YouTube, Amarillo en negrita, Con contorno, TikTok) en la sección **Subtítulo**.',
            'Ajusta el color, el tamaño, la posición vertical y un fondo opcional del subtítulo generado.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Usa **Consolidar subtítulos en segmento** para convertir los subtítulos generados de la transcripción en un segmento de subtítulos independiente.',
        },
      ],
    },
    {
      title: 'Subtítulos incrustados e importados',
      blocks: [
        {
          kind: 'list',
          items: [
            'Usa **Extraer subtítulos incrustados** en una tarjeta de medio para buscar pistas de subtítulos en un archivo.',
            'En el diálogo **Subtítulos incrustados**, elige una pista; cada fila muestra su idioma, códec y cantidad de cues.',
            'Inserta la pista para agregar sus cues a la línea de tiempo como un segmento de subtítulos.',
            'Edita el inicio, el final y el texto de cada cue en la sección **Subtítulo**, y define Cursiva, Negrita o Subrayado para el cue completo.',
          ],
        },
      ],
    },
    {
      title: 'Subtítulos al exportar',
      blocks: [
        {
          kind: 'note',
          tone: 'info',
          text: 'Activa **Incrustar subtítulos** en los ajustes de exportación para incluir los subtítulos generados de la transcripción como una pista de subtítulos (MP4, MKV, WebM). Algunos reproductores ocultan las pistas de subtítulos de forma predeterminada, así que los espectadores podrían necesitar activarlas.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
